import { useMemo, useState } from 'react';
import { Button, Card, CardContent, Input } from '@tark/shared/components';
import { useCoachMatchPlay, useSchedulePrograms } from '@tark/shared/hooks';
import type { TenantMatchPlayCoachProposal } from '@tark/shared/types';
import { CheckCircle2, Loader2, UsersRound } from 'lucide-react';
import CoachPageHeader from '../components/CoachPageHeader';

export default function CoachMatchPlayPage() {
  const matchPlay = useCoachMatchPlay();
  const programs = useSchedulePrograms();
  const [selected, setSelected] = useState<number[]>([]);
  const [scheduling, setScheduling] = useState<TenantMatchPlayCoachProposal | null>(null);
  const [programId, setProgramId] = useState<number | null>(null);
  const [startsAt, setStartsAt] = useState(defaultStart);
  const [location, setLocation] = useState('');
  const error = matchPlay.dashboard.isError || matchPlay.propose.isError || matchPlay.schedule.isError;
  const athletes = matchPlay.dashboard.data?.optedInAthletes ?? [];
  const proposals = matchPlay.dashboard.data?.proposals ?? [];
  const activePrograms = useMemo(() => (programs.data ?? []).filter((item) => item.active !== false), [programs.data]);

  const toggle = (athleteId: number) => setSelected((current) => current.includes(athleteId)
    ? current.filter((id) => id !== athleteId)
    : current.length < 2 ? [...current, athleteId] : [current[1], athleteId]);

  const propose = () => {
    if (selected.length !== 2) return;
    matchPlay.propose.mutate({ athleteOneId: selected[0], athleteTwoId: selected[1] }, { onSuccess: () => setSelected([]) });
  };

  const schedule = () => {
    if (!scheduling || !programId || !startsAt) return;
    const start = new Date(startsAt);
    if (Number.isNaN(start.getTime()) || start.getTime() <= Date.now() + 5 * 60 * 1000) return;
    matchPlay.schedule.mutate({
      proposalId: scheduling.proposalId,
      programId,
      startsAt: start.toISOString(),
      endsAt: new Date(start.getTime() + 60 * 60 * 1000).toISOString(),
      locationLabel: location.trim() || undefined,
    }, { onSuccess: () => { setScheduling(null); setLocation(''); } });
  };

  return (
    <div className="w-full space-y-6 pb-24 md:pb-8">
      <CoachPageHeader title="Match play" subtitle="Connect two opted-in students. Families approve anonymously before any optional profile details are shared." />
      {error ? <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">Match play could not be updated. Refresh and try again.</p> : null}
      {matchPlay.dashboard.isLoading ? (
        <div className="flex min-h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
          <Card variant="default">
            <CardContent className="space-y-3 p-5">
              <div>
                <h2 className="text-lg font-semibold text-text">Choose two students</h2>
                <p className="mt-1 text-sm text-text-muted">Only students whose family opted in appear here.</p>
              </div>
              {athletes.length < 2 ? (
                <div className="rounded-lg border border-dashed border-border p-5 text-sm text-text-muted">Two family opt-ins are needed before suggesting a match.</div>
              ) : athletes.map((athlete) => {
                const chosen = selected.includes(athlete.athleteId);
                return (
                  <button key={athlete.athleteId} type="button" onClick={() => toggle(athlete.athleteId)}
                    className={`flex min-h-16 w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition ${chosen ? 'border-primary bg-primary/10' : 'border-border bg-surface hover:border-primary/40'}`}>
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 font-bold text-primary">{athlete.displayName.slice(0, 1)}</span>
                    <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-text">{athlete.displayName}</span><span className="mt-1 block text-xs text-text-muted">{[athlete.levelCode, athlete.ageBand].filter(Boolean).join(' · ') || 'Match play'}</span></span>
                    {chosen ? <CheckCircle2 className="h-5 w-5 text-primary" /> : null}
                  </button>
                );
              })}
              {athletes.length >= 2 ? <Button disabled={selected.length !== 2} isLoading={matchPlay.propose.isPending} onClick={propose}>{selected.length === 2 ? 'Suggest this match' : `Choose ${2 - selected.length} more`}</Button> : null}
            </CardContent>
          </Card>

          <Card variant="default">
            <CardContent className="space-y-3 p-5">
              <h2 className="text-lg font-semibold text-text">Suggestions</h2>
              {proposals.length === 0 ? <p className="text-sm text-text-muted">No match suggestions yet.</p> : proposals.map((proposal) => (
                <div key={proposal.proposalId} className="flex min-h-16 items-center gap-3 border-t border-border py-3 first:border-0">
                  <UsersRound className="h-5 w-5 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-text">{proposal.participants.map((item) => item.displayName).join(' & ')}</p><p className="mt-1 text-xs text-text-muted">{statusLabel(proposal.status)}</p></div>
                  {proposal.status === 'READY_FOR_LOGISTICS' ? <Button size="sm" variant="outline" onClick={() => { setScheduling(proposal); setProgramId(activePrograms[0]?.programId ?? null); }}>Set time</Button> : null}
                  {proposal.status === 'SCHEDULED' ? <CheckCircle2 className="h-5 w-5 text-primary" /> : null}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {scheduling ? (
        <Card variant="default" className="max-w-2xl">
          <CardContent className="space-y-4 p-5">
            <div><h2 className="text-lg font-semibold text-text">Schedule approved match</h2><p className="mt-1 text-sm text-text-muted">A session is created only after you confirm these details.</p></div>
            <label className="block text-sm font-medium text-text">Class<select className="mt-1.5 h-10 w-full rounded-lg border border-border bg-surface px-3" value={programId ?? ''} onChange={(event) => setProgramId(Number(event.target.value) || null)}><option value="">Choose class</option>{activePrograms.map((program) => <option key={program.programId} value={program.programId}>{program.name}</option>)}</select></label>
            <Input label="Starts" type="datetime-local" min={minimumStart()} value={startsAt} onChange={(event) => setStartsAt(event.target.value)} />
            <Input label="Court or location" value={location} onChange={(event) => setLocation(event.target.value)} />
            <div className="flex gap-2"><Button isLoading={matchPlay.schedule.isPending} disabled={!programId || !startsAt} onClick={schedule}>Schedule match</Button><Button variant="outline" onClick={() => setScheduling(null)}>Cancel</Button></div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function statusLabel(status: string) {
  return ({ PENDING_APPROVAL: 'Waiting for family approval', READY_FOR_LOGISTICS: 'Both families approved', SCHEDULED: 'Scheduled', DECLINED: 'Declined', CANCELLED: 'Closed' } as Record<string, string>)[status] ?? 'Match suggestion';
}
function localInput(date: Date) { const offset = date.getTimezoneOffset(); return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 16); }
function defaultStart() { const date = new Date(); date.setDate(date.getDate() + 1); date.setHours(16, 0, 0, 0); return localInput(date); }
function minimumStart() { return localInput(new Date(Date.now() + 6 * 60 * 1000)); }
